import type { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

/**
 * The boolean part of the program page's client-side showing filters, as a table instead of one
 * hand-written `if` per flag.
 *
 * A flag missing from this table gets NO predicate at all, which fails silently in two directions: its
 * chip filters nothing, and `hasMatchingShowing` (which keeps an option only if some showing passes its
 * predicates) goes vacuously true over the empty predicate list and offers the chip at cinemas that have
 * no such showing. So a new technology flag belongs here, in one place, and not in a hand-written `if`.
 *
 * Deliberately free of runtime imports: `frontend/test/` runs with no node_modules (see the
 * "[frontend] Tests" pipeline step and tests-run-without-install.test.mjs), so only a module whose
 * imports are all type-only — erased before Node sees them — can be covered there. That rules out
 * the rest of getShowingFilters, whose time-of-day branches need `getDateInTimezone` and, through
 * it, `@cineamo/ui`.
 */
export const BOOLEAN_SHOWING_FILTER_KEYS = [
    'isOriginalLanguage',
    'isSubtitled',
    'isDubbed',
    'isDolbyAtmos',
    'isImax',
    'isDolbyVision',
    'isDolbyCinema',
    'is4DX',
    'isScreenX',
    'isHFR',
    'isLive',
    'isDbox',
    'isInfinityVision'
] as const;

/**
 * `isThreeDimensional` is the one flag matched on its COERCED value: `2D` has to return the showings
 * where the flag is null or absent, not only the ones explicitly stored as false. Kept apart rather
 * than folded into the table above so that difference stays visible instead of looking like a typo.
 */
export const COERCED_BOOLEAN_SHOWING_FILTER_KEYS = ['isThreeDimensional'] as const;

export type BooleanShowingFilterKey =
    | (typeof BOOLEAN_SHOWING_FILTER_KEYS)[number]
    | (typeof COERCED_BOOLEAN_SHOWING_FILTER_KEYS)[number];

/** URL values arrive as the strings `'true'`/`'false'`; the same filters are also built from booleans. */
export function toShowingFilterBoolean(value: boolean | string | undefined): boolean {
    return typeof value === 'boolean' ? value : value === 'true';
}

/**
 * One predicate per boolean flag present in the query. Keys the query does not carry are skipped,
 * so an empty query yields an empty list — the caller's `every` then passes every showing, which is
 * what "no filter selected" means there.
 */
export function buildBooleanShowingFilters(
    query: Partial<Record<BooleanShowingFilterKey, boolean | string>>
): ((showing: ShowingDto) => boolean)[] {
    const hasQueryValue = (key: BooleanShowingFilterKey) =>
        key in query && query[key] !== undefined && query[key] !== null;

    const filters: ((showing: ShowingDto) => boolean)[] = [];

    for (const key of BOOLEAN_SHOWING_FILTER_KEYS) {
        if (hasQueryValue(key)) {
            const expected = toShowingFilterBoolean(query[key]);
            filters.push((showing) => showing[key] === expected);
        }
    }

    for (const key of COERCED_BOOLEAN_SHOWING_FILTER_KEYS) {
        if (hasQueryValue(key)) {
            const expected = toShowingFilterBoolean(query[key]);
            filters.push((showing) => Boolean(showing[key]) === expected);
        }
    }

    return filters;
}
